import { useState } from 'react';
import { 
  Phone, Mail, MapPin, Clock, CheckCircle, ArrowRight, 
  AlertTriangle, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    urgency: 'normal',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setFormData({ name: '', email: '', phone: '', address: '', service: '', urgency: 'normal', message: '' });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      info: '+1-888-356-8891',
      link: 'tel:+18883568891',
      desc: 'Call us anytime'
    },
    {
      icon: Mail,
      title: 'Email',
      info: 'support@primespick.store',
      link: 'mailto:support@primespick.store',
      desc: 'We reply within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Address',
      info: '1284 Silver Lake Road',
      info2: 'Minneapolis, MN 44401',
      desc: 'Visit our office'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      info: 'Mon - Sat: 8:00 AM - 6:00 PM',
      info2: 'Sunday: Closed',
      desc: 'We are here to help'
    }
  ];

  return (
    <div className="page-transition pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="contact-hero">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Us
            </h1>
            <p className="text-lg text-[#52525B]">
              Have a pest problem? Get in touch with our expert team for a free consultation 
              and quote. We are here to help you achieve a pest-free environment.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12" data-testid="contact-info">
        <div className="container-main">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-lg border border-border hover:border-[#2E7D32]/30 transition-colors"
                data-testid={`contact-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#2E7D32]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1C1A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                {item.link ? (
                  <a href={item.link} className="text-[#2E7D32] font-medium hover:underline block">
                    {item.info}
                  </a>
                ) : (
                  <>
                    <p className="text-[#1A1C1A] font-medium">{item.info}</p>
                    {item.info2 && <p className="text-[#1A1C1A] font-medium">{item.info2}</p>}
                  </>
                )}
                <p className="text-sm text-[#52525B] mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="py-6" data-testid="emergency-notice">
        <div className="container-main">
          <div className="bg-[#F9A825]/10 border border-[#F9A825]/30 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 bg-[#F9A825] rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg font-semibold text-[#1A1C1A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Emergency Pest Service Available
              </h3>
              <p className="text-[#52525B]">
                For urgent pest emergencies, call us immediately. We offer same-day service for critical situations.
              </p>
            </div>
            <a href="tel:+18883568891" className="btn-accent flex-shrink-0" data-testid="emergency-call-btn">
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding" data-testid="contact-form-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Send Us a Message
              </h2>
              
              {formSubmitted ? (
                <div className="bg-[#E8F5E9] rounded-2xl p-8 text-center" data-testid="contact-form-success">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1C1A] mb-2">Message Sent Successfully!</h3>
                  <p className="text-[#52525B] mb-6">
                    Thank you for contacting us. One of our pest control experts will get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setFormSubmitted(false)} 
                    className="btn-secondary"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Full Name *</label>
                      <Input
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="form-input-focus"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Phone Number *</label>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                        className="form-input-focus"
                        data-testid="contact-input-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Email Address *</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="form-input-focus"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Property Address</label>
                    <Input
                      type="text"
                      placeholder="123 Main St, Minneapolis, MN"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="form-input-focus"
                      data-testid="contact-input-address"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Service Needed</label>
                      <select
                        value={formData.service}
                        onChange={(e) => setFormData({...formData, service: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        data-testid="contact-select-service"
                      >
                        <option value="">Select a service</option>
                        <option value="general">General Pest Control</option>
                        <option value="termite">Termite Control</option>
                        <option value="rodent">Rodent Control</option>
                        <option value="bedbug">Bed Bug Treatment</option>
                        <option value="ant">Ant Control</option>
                        <option value="cockroach">Cockroach Control</option>
                        <option value="mosquito">Mosquito Control</option>
                        <option value="commercial">Commercial Services</option>
                        <option value="inspection">Free Inspection</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Urgency Level</label>
                      <select
                        value={formData.urgency}
                        onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        data-testid="contact-select-urgency"
                      >
                        <option value="normal">Normal - Schedule at convenience</option>
                        <option value="soon">Soon - Within a few days</option>
                        <option value="urgent">Urgent - ASAP</option>
                        <option value="emergency">Emergency - Same day</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Tell Us About Your Pest Problem</label>
                    <Textarea
                      placeholder="Please describe the pest issue you're experiencing, including what type of pest, where you've seen them, and how long the problem has existed..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={5}
                      className="form-input-focus"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full" data-testid="contact-submit-btn">
                    <Send className="w-5 h-5" />
                    Send Message
                  </Button>
                </form>
              )}
            </div>

            {/* Map */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Find Us
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-lg h-[400px] lg:h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d180776.75892508587!2d-93.4037!3d44.9706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b333909377bbbd%3A0x939fc9842f7aee07!2sMinneapolis%2C%20MN!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Primes Pick Location"
                ></iframe>
              </div>
              <div className="mt-6 bg-[#F7F9F7] rounded-2xl p-6">
                <h3 className="font-semibold text-[#1A1C1A] mb-3">Primes Pick Pest Control</h3>
                <p className="text-[#52525B] mb-2">
                  <MapPin className="w-4 h-4 inline mr-2 text-[#2E7D32]" />
                  1284 Silver Lake Road, Minneapolis, MN 44401
                </p>
                <p className="text-[#52525B] mb-2">
                  <Clock className="w-4 h-4 inline mr-2 text-[#2E7D32]" />
                  Mon - Sat: 8:00 AM - 6:00 PM | Sunday: Closed
                </p>
                <p className="text-[#52525B]">
                  <Phone className="w-4 h-4 inline mr-2 text-[#2E7D32]" />
                  <a href="tel:+18883568891" className="hover:text-[#2E7D32]">+1-888-356-8891</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16 sm:py-20" data-testid="contact-cta">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Prefer to Talk to Someone?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Our friendly team is ready to answer your questions and schedule your free inspection.
          </p>
          <a href="tel:+18883568891" className="btn-accent inline-flex" data-testid="contact-cta-call">
            <Phone className="w-5 h-5" />
            Call Now: +1-888-356-8891
          </a>
        </div>
      </section>
    </div>
  );
}
